import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Project } from '../shared/models/project';
import { Observable } from 'rxjs';
import { ApiUrlProvider } from './api-url.provider';
import { IApiResult } from '../shared/models/api-result';
import { IJobCode } from '../shared/models/job-code';
import { PpsUtilityService } from './pps-utility.service';
import { ProjectSearchModel } from '../shared/models/ProjectSearchModel';
import { ProjectEntities } from '../shared/models/project-entities';
import { IProjectComment } from '../shared/models/project-comment';
import { IDocument } from '../shared/models/document';
import { IProjectSimpleModel } from '../shared/models/project-simple-model';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  constructor(
    private http: HttpClient,
    private util: PpsUtilityService,
    private apiUrlProvider: ApiUrlProvider) { }

  getJobCodes(): Observable<IApiResult<IJobCode[]>>{
    return this.http.get<IApiResult<IJobCode[]>>(`${this.apiUrlProvider.url}/jobcode`);
  }

  getProjects(projectSearchModel: ProjectSearchModel): Observable<IApiResult<Project[]>>{
    return this.http.get<IApiResult<Project[]>>(`${this.apiUrlProvider.url}/project`, {
      params: projectSearchModel as any
    });
  }

  getProjectEntities(): Observable<IApiResult<ProjectEntities>>{
    return this.http.get<IApiResult<ProjectEntities>>(`${this.apiUrlProvider.url}/project/Entities`);
  }

  getSimpleProjects(): Observable<IApiResult<IProjectSimpleModel[]>>{
    return this.http.get<IApiResult<IProjectSimpleModel[]>>(`${this.apiUrlProvider.url}/project/simple`);
  }

  downloadProjects(searchModel: ProjectSearchModel, whenDone){
    let url = `${this.apiUrlProvider.url}/project/download`;

    return this.http
      .get(url, {params: searchModel as any, responseType: 'blob'})
      .subscribe(res => {
        let objectUrl = window.URL.createObjectURL(res);
        let imageAnchor: HTMLAnchorElement = document.createElement("a");
        imageAnchor.href = objectUrl;
        imageAnchor.target = "_blank";
        imageAnchor.download = `projects.xlsx`;
        imageAnchor.click();
        window.URL.revokeObjectURL(objectUrl);
      }, _ => {})
      .add(()=>{
        if (whenDone){
          whenDone();
        }
      });
  }

  getProjectColumnAccessPermission(userId: string){
    return this.http.get<IApiResult<any>>(`${this.apiUrlProvider.url}/project/ColumnAccessPermission`);
  }

  //ionos.com seems not support HttpPut verb. So add support to use HttpPost to update project
  updateProject(project: Project): Observable<IApiResult<Project>>{
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this.http.post<IApiResult<Project>>(`${this.apiUrlProvider.url}/project/${project.id}/update`, project, httpOptions);
  }

  //ionos.com seems not support HttpPut verb. So add support to use HttpPost to update project
  updateProjectWithPut(project: Project): Observable<IApiResult<Project>>{
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this.http.put<IApiResult<Project>>(`${this.apiUrlProvider.url}/project`, project, httpOptions);
  }

  addProject(project: Project): Observable<IApiResult<Project>>{
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this.http.post<IApiResult<Project>>(`${this.apiUrlProvider.url}/project`, project, httpOptions);
  }

  deleteProject(projectId: number): Observable<IApiResult<string>>{
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/project/${projectId}/delete`, null);
  }

  deleteProjectInvoice(projectId: number, invoiceId: number): Observable<IApiResult<string>>{
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/project/${projectId}/invoice/${invoiceId}/delete`, null);
  }

  deleteProjectWithHttpDelete(projectId: number): Observable<IApiResult<string>>{
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this.http.delete<IApiResult<string>>(`${this.apiUrlProvider.url}/project/${projectId}`);
  }

  getNextProjectNo(projects: Project[]): string{
    let numberArray = projects.map(p => {
      if (p.projectNo.indexOf("PID-") !== 0){
        return 0;
      }else{
        return parseInt(p.projectNo.split("-")[1]);
      }
    });

    const maxNumber = numberArray.length === 0 ? 0 : Math.max.apply(Math, numberArray);
    
    return `PID-${this.util.padNumber(maxNumber + 1, 4, "0")}`;
  }

  addProjectComment(projectId: number, comment: IProjectComment){
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this.http.post<IApiResult<number>>(`${this.apiUrlProvider.url}/project/${projectId}/comment`, comment, httpOptions);
  }

  getProjectComments(projectId: number): Observable<IApiResult<IProjectComment[]>>{
    return this.http.get<IApiResult<IProjectComment[]>>(`${this.apiUrlProvider.url}/project/${projectId}/comment`);
  }

  getProjectAttachments(projectId: number): Observable<IApiResult<IDocument[]>>{
    return this.http.get<IApiResult<IDocument[]>>(`${this.apiUrlProvider.url}/project/${projectId}/attachment`);
  }

  getAttachmentUploadUrl(projectId: number){
    return `${this.apiUrlProvider.url}/project/${projectId}/attachment`;
  }

  deleteProjectAttachment(projectId: number, filename: string): Observable<IApiResult<boolean>>{
    var url = `${this.apiUrlProvider.url}/project/${projectId}/attachment/delete/${filename}`;
    return this.http.post<IApiResult<boolean>>(url, {});
  }
}
